import type { SmsProviderSummary, TrackingAdapterSummary, WhatsappProviderSummary } from '@chukua/common';
import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router';
import { installGpsIntegrationPath } from '../features/gps/gps.paths';
import { installSmsIntegrationPath } from '../features/sms/sms.paths';
import { installWhatsappIntegrationPath } from '../features/whatsapp/whatsapp.paths';
import { INTEGRATION_KIND_LABELS, type IntegrationKind } from '../integrations.paths';
import { IntegrationLogo } from './IntegrationLogo';

const EMPTY_GROUP_TEXT: Record<IntegrationKind, string> = {
  sms: 'Every SMS provider in this release is installed.',
  whatsapp: 'Every WhatsApp provider in this release is installed.',
  gps: 'No GPS adapters in this release.',
};

interface CatalogueEntry {
  key: string;
  displayName: string;
  logoSvg: string;
  to: string;
}

interface IntegrationCatalogueProps {
  /** SMS providers not installed yet; an installed one is opened from its row instead. */
  smsProviders: SmsProviderSummary[];
  /** WhatsApp providers not installed yet, likewise. */
  whatsappProviders: WhatsappProviderSummary[];
  gpsAdapters: TrackingAdapterSummary[];
  /** Only this kind, when opened from its section. */
  kind?: IntegrationKind;
}

/** What this release can connect to, grouped by kind; each entry opens its install form. */
export function IntegrationCatalogue({ smsProviders, whatsappProviders, gpsAdapters, kind }: IntegrationCatalogueProps) {
  const groups: { kind: IntegrationKind; entries: CatalogueEntry[] }[] = [
    { kind: 'sms', entries: smsProviders.map((provider) => ({ ...provider, to: installSmsIntegrationPath(provider.key) })) },
    { kind: 'whatsapp', entries: whatsappProviders.map((provider) => ({ ...provider, to: installWhatsappIntegrationPath(provider.key) })) },
    { kind: 'gps', entries: gpsAdapters.map((adapter) => ({ ...adapter, to: installGpsIntegrationPath(adapter.key) })) },
  ];

  return (
    <div className="flex flex-col gap-6">
      {groups
        .filter((group) => !kind || group.kind === kind)
        .map((group) => (
          <section key={group.kind} aria-label={INTEGRATION_KIND_LABELS[group.kind]} className="flex flex-col gap-2">
            <h3 className="text-xs font-semibold tracking-wide text-muted uppercase">{INTEGRATION_KIND_LABELS[group.kind]}</h3>
            {group.entries.length === 0 ? (
              <p className="text-sm text-muted">{EMPTY_GROUP_TEXT[group.kind]}</p>
            ) : (
              <ul className="grid gap-2 sm:grid-cols-2">
                {group.entries.map((entry) => (
                  <li key={entry.key}>
                    <Link to={entry.to} className="flex items-center gap-3 rounded-lg border border-line bg-surface p-3 hover:border-primary">
                      <IntegrationLogo logoSvg={entry.logoSvg} name={entry.displayName} />
                      <span className="flex-1 font-semibold text-fg">{entry.displayName}</span>
                      <ChevronRight className="size-4 text-muted" aria-hidden />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}
    </div>
  );
}
